import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const api = useApi();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(isEditMode);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [roles, setRoles] = useState([]);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    roleId: '',
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  const loadRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const response = await api.get('/users/roles');
      const apiResponse = response.data;
      
      if (apiResponse?.success && apiResponse?.data?.roles) {
        setRoles(apiResponse.data.roles);
      } else if (apiResponse?.roles) {
        setRoles(apiResponse.roles);
      } else {
        setRoles([
          { roleId: 1, roleName: 'JLL Super Admin', roleCode: 'JLL_SUPER_ADMIN' },
          { roleId: 2, roleName: 'JLL Analyst', roleCode: 'JLL_ANALYST' },
          { roleId: 3, roleName: 'Client Admin', roleCode: 'CLIENT_ADMIN' },
          { roleId: 4, roleName: 'Client Power User', roleCode: 'CLIENT_POWER_USER' },
          { roleId: 5, roleName: 'Client Viewer', roleCode: 'CLIENT_VIEWER' },
        ]);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setRoles([
        { roleId: 1, roleName: 'JLL Super Admin', roleCode: 'JLL_SUPER_ADMIN' },
        { roleId: 2, roleName: 'JLL Analyst', roleCode: 'JLL_ANALYST' },
        { roleId: 3, roleName: 'Client Admin', roleCode: 'CLIENT_ADMIN' },
        { roleId: 4, roleName: 'Client Power User', roleCode: 'CLIENT_POWER_USER' },
        { roleId: 5, roleName: 'Client Viewer', roleCode: 'CLIENT_VIEWER' },
      ]);
    } finally {
      setLoadingRoles(false);
    }
  }, [api]);

  const loadUser = useCallback(async () => {
    try {
      setLoadingUser(true);
      const response = await api.get(`/users/${id}`);
      const apiResponse = response.data;

      if (apiResponse?.success && apiResponse?.data) {
        const user = apiResponse.data;
        setFormData({
          email: user.email || '',
          fullName: user.fullName || user.displayName || '',
          roleId: user.roles?.[0]?.roleId || '',
          isActive: user.isActive ?? true,
        });
      } else if (apiResponse) {
        setFormData({
          email: apiResponse.email || '',
          fullName: apiResponse.fullName || apiResponse.displayName || '',
          roleId: apiResponse.roles?.[0]?.roleId || '',
          isActive: apiResponse.isActive ?? true,
        });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load user'));
    } finally {
      setLoadingUser(false);
    }
  }, [api, id]);

  useEffect(() => {
    loadRoles();
    if (isEditMode) {
      loadUser();
    }
  }, [isEditMode, loadRoles, loadUser]);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!isEditMode) {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }

      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      } else if (formData.fullName.length < 2) {
        newErrors.fullName = 'Full name must be at least 2 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        const updateData = {
          isActive: formData.isActive,
        };
        if (formData.roleId) {
          updateData.roleId = formData.roleId;
        }
        await api.put(`/users/${id}`, updateData);
      } else {
        const createData = {
          email: formData.email,
          fullName: formData.fullName,
        };
        if (formData.roleId) {
          createData.roleId = formData.roleId;
        }
        await api.post('/users', createData);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/users');
      }, 1500);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save user'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  if (loadingUser || loadingRoles) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEditMode ? 'Edit User Role' : 'Create New User'}
      </Typography>

      {isEditMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only the user's role can be updated. Email and name cannot be changed.
        </Typography>
      )}

      <Paper sx={{ p: 4, mt: 2 }}>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            User {isEditMode ? 'updated' : 'created'} successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={isEditMode ? 'Email cannot be changed' : errors.email}
                required={!isEditMode}
                disabled={isEditMode}
                InputProps={{
                  readOnly: isEditMode,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                error={!!errors.fullName}
                helperText={isEditMode ? 'Name cannot be changed' : errors.fullName}
                required={!isEditMode}
                disabled={isEditMode}
                InputProps={{
                  readOnly: isEditMode,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.roleId}>
                <InputLabel>Role (Optional)</InputLabel>
                <Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  label="Role (Optional)"
                >
                  <MenuItem value="">
                    <em>None - Assign role later</em>
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.roleId || role.RoleId} value={role.roleId || role.RoleId}>
                      {role.roleName || role.RoleName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.roleId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.roleId}
                  </Typography>
                )}
                {!isEditMode && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2 }}>
                    You can assign roles to tenants later from Client Management
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {isEditMode && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Active"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Inactive users cannot log in to the system
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  sx={{ backgroundColor: '#000', '&:hover': { backgroundColor: '#333' } }}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Role' : 'Create User'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default UserForm;
